import React, { useState } from 'react';
import { Language } from '../types/kundali';
import { Sparkles, Zap, Send, Loader2, Copy, Check, AlertTriangle, Shield, Info } from 'lucide-react';

interface AcharyaSanctuaryContainerProps {
  lang: Language;
  aiQuota: { remaining: number; allowance: number } | null;
  customQuestionInput: string;
  onCustomQuestionChange: (val: string) => void;
  onRequestAiAnalysis: () => void;
  isAiLoading: boolean;
  aiInterpretation: string;
  aiError: string;
  className?: string;
}

export const AcharyaSanctuaryContainer: React.FC<AcharyaSanctuaryContainerProps> = ({
  lang,
  aiQuota,
  customQuestionInput,
  onCustomQuestionChange,
  onRequestAiAnalysis,
  isAiLoading,
  aiInterpretation,
  aiError,
  className = ''
}) => {
  const isHi = lang === 'hi';
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!aiInterpretation) return;
    navigator.clipboard.writeText(aiInterpretation);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      id="acharya-sanctuary-container"
      className={`rounded-2xl border border-astro-border bg-astro-card p-5 sm:p-6 shadow-2xs space-y-4 ${className}`}
    >
      {/* Sanctuary Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-astro-border pb-3.5">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-astro-accent" />
            <h3 className="text-sm sm:text-base font-bold uppercase tracking-wider text-astro-text">
              {isHi ? 'आचार्य एआई परामर्श' : 'Acharya AI Counsel'}
            </h3>
          </div>
          <p className="text-xs text-astro-secondary-text mt-0.5">
            {isHi
              ? 'शास्त्रीय जन्मजात फल, विंशोत्तरी दशा व गोचर संमिलन पर आधारित व्यक्तिगत परामर्शीय संवाद।'
              : 'Synthesized, compassionate Vedic astrological consultation grounded in classical Parashari principles.'}
          </p>
        </div>

        {/* Quota Badge */}
        {aiQuota && (
          <div className="flex items-center gap-2 bg-astro-bg px-3 py-1.5 rounded-xl border border-astro-border text-xs shadow-2xs self-start sm:self-auto">
            <Zap className="w-3.5 h-3.5 text-astro-accent" />
            <span className="text-astro-secondary-text">
              {isHi ? 'मासिक कोटा:' : 'Monthly Quota:'}
            </span>
            <span className="font-bold text-astro-primary font-mono">
              {aiQuota.remaining} / {aiQuota.allowance} {isHi ? 'शेष' : 'left'}
            </span>
          </div>
        )}
      </div>

      {/* Interactive Prompt & Generate Trigger */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
        <input
          type="text"
          value={customQuestionInput}
          onChange={(e) => onCustomQuestionChange(e.target.value)}
          placeholder={
            isHi
              ? 'वैकल्पिक: कोई विशिष्ट जीवन संदर्भ, निर्णय या अतिरिक्त प्रश्न लिखें...'
              : 'Optional: Add specific native context, life dilemmas or questions...'
          }
          className="flex-1 px-4 py-2.5 rounded-xl border border-astro-border bg-astro-bg text-xs text-astro-text placeholder-[#4B5956]/60 focus:outline-none focus:ring-2 focus:ring-astro-primary/30 min-h-[44px]"
        />
        <button
          onClick={onRequestAiAnalysis}
          disabled={isAiLoading}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-astro-primary hover:bg-astro-secondary active:bg-astro-secondary text-white rounded-xl text-xs font-bold shadow-2xs transition-all disabled:opacity-50 whitespace-nowrap min-h-[44px] min-w-[44px] touch-manipulation cursor-pointer"
        >
          {isAiLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {isHi ? 'आचार्य चिंतन जारी...' : 'Consulting Acharya...'}
            </>
          ) : (
            <>
              <Send className="w-4 h-4 text-astro-accent" />
              {isHi ? 'आचार्य परामर्श प्राप्त करें' : 'Seek Acharya Counsel'}
            </>
          )}
        </button>
      </div>

      {/* Error Message */}
      {aiError && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{aiError}</span>
        </div>
      )}

      {/* Generated Acharya Output */}
      {aiInterpretation && (
        <div className="bg-astro-bg border border-astro-border rounded-xl p-5 shadow-2xs space-y-3 relative animate-fadeIn">
          <div className="flex items-center justify-between border-b border-astro-border pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-astro-primary">
              {isHi ? 'वैदिक ज्योतिषाचार्य फलादेश' : 'Vedic Acharya Consultation Reading'}
            </span>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 text-xs text-astro-secondary-text hover:text-astro-text p-1.5 rounded min-h-[36px] touch-manipulation cursor-pointer"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? (isHi ? 'कॉपी किया!' : 'Copied!') : (isHi ? 'कॉपी करें' : 'Copy')}</span>
            </button>
          </div>
          <div className="text-xs sm:text-sm leading-relaxed text-astro-text whitespace-pre-line font-serif">
            {aiInterpretation}
          </div>
        </div>
      )}

      {/* Mandatory Disclaimer Footer */}
      <div className="pt-2.5 border-t border-astro-border space-y-1 text-[11px] text-astro-secondary-text">
        <div className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-astro-primary">
          <Shield className="w-3.5 h-3.5 text-astro-accent shrink-0" />
          <span>
            {isHi
              ? 'ज्योतिषाचार्य एआई परामर्श — प्रत्यक्ष जन्मकुंडली साक्ष्य आधारित'
              : 'JYOTISH ACHARYA AI COUNSEL — Grounded in Deterministic Chart Evidence'}
          </span>
        </div>
        <p className="leading-relaxed">
          {isHi
            ? 'एआई केवल प्रासंगिक परामर्श का संश्लेषण करता है एवं ग्रहीय गणितीय गणना नहीं करता। पुरुषार्थ, विवेक व कर्म से प्रारब्ध संवरता है।'
            : 'AI synthesizes contextual counsel and does not compute planetary data. Grounded in classical Parashari chart evidence. Free will (Purushartha) and dharmic action shape destiny.'}
        </p>
      </div>
    </div>
  );
};
